// AntivirusProfiles.tsx
import { useState, useEffect, useCallback } from "react";
import {
    Loader2, Shield, ChevronDown,
} from "lucide-react";
import { showError } from "../../../../../utils/toast";
import baseapi from "../../../../../services/baseapi";

// ── Types ─────────────────────────────────────────────────────────────────────
interface ProtocolConfig {
    "av-scan"?: string;
    "emulator"?: string;
    "fortindr"?: string;
    "quarantine"?: string;
    "fortisandbox"?: string;
    "external-blocklist"?: string;
    "outbreak-prevention"?: string;
    "content-disarm"?: string;
    "executables"?: string;
    [key: string]: any;
}

interface AVProfile {
    name: string;
    comment: string;
    "scan-mode": string;
    "feature-set": string;
    "av-virus-log": string;
    "extended-log": string;
    "mobile-malware-db": string;
    "ems-threat-feed": string;
    "fortisandbox-mode": string;
    "nac-quar": { log: string; expiry: string; infected: string };
    http?: ProtocolConfig;
    ftp?: ProtocolConfig;
    smtp?: ProtocolConfig;
    pop3?: ProtocolConfig;
    imap?: ProtocolConfig;
    mapi?: ProtocolConfig;
    ssh?: ProtocolConfig;
    cifs?: ProtocolConfig;
    nntp?: ProtocolConfig;
    [key: string]: any;
}

// ── Protocol list to show as rows ─────────────────────────────────────────────
const PROTOCOLS = ["http", "ftp", "smtp", "pop3", "imap", "mapi", "ssh", "cifs", "nntp"];

// ── Value Chip ────────────────────────────────────────────────────────────────
const ValChip = ({ val }: { val: string | undefined }) => {
    if (!val || val === "") return <span className="text-slate-400 dark:text-gray-500 text-[9px]">—</span>;

    const style =
        val === "enable" || val === "block"
            ? "bg-green-500/10 border border-green-500/20 text-green-600 dark:text-green-400"
            : val === "disable" || val === "none"
                ? "bg-slate-100 dark:bg-gray-700/40 border border-slate-200 dark:border-gray-600/30 text-slate-500 dark:text-gray-400"
                : val === "monitor"
                    ? "bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-400"
                    : val === "quarantine"
                        ? "bg-amber-500/10 border border-amber-500/20 text-amber-600 dark:text-yellow-400"
                        : "text-slate-700 dark:text-gray-300";

    return (
        <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[8px] font-bold uppercase whitespace-nowrap ${style}`}>
            {val}
        </span>
    );
};

// ── Collapsible Profile Table ─────────────────────────────────────────────────
const PROTOCOL_FIELDS: { key: keyof ProtocolConfig; label: string }[] = [
    { key: "av-scan", label: "AV Scan" },
    { key: "outbreak-prevention", label: "Outbreak Prev." },
    { key: "emulator", label: "Emulator" },
    { key: "fortindr", label: "FortiNDR" },
    { key: "fortisandbox", label: "FortiSandbox" },
    { key: "quarantine", label: "Quarantine" },
    { key: "external-blocklist", label: "Ext. Blocklist" },
    { key: "content-disarm", label: "Content Disarm" },
    { key: "executables", label: "Executables" },
];

const ProfileTable = ({ profile, index }: { profile: AVProfile; index: number }) => {
    const [open, setOpen] = useState(true);

    const PROFILE_COLORS = [
        { accent: "text-blue-600 dark:text-blue-400", border: "border-blue-200 dark:border-blue-500/30", bg: "bg-blue-50/50 dark:bg-blue-500/5", icon: "bg-blue-500/10 border-blue-500/20" },
        { accent: "text-purple-600 dark:text-purple-400", border: "border-purple-200 dark:border-purple-500/30", bg: "bg-purple-50/50 dark:bg-purple-500/5", icon: "bg-purple-500/10 border-purple-500/20" },
        { accent: "text-green-600 dark:text-green-400", border: "border-green-200 dark:border-green-500/30", bg: "bg-green-50/50 dark:bg-green-500/5", icon: "bg-green-500/10 border-green-500/20" },
        { accent: "text-orange-600 dark:text-orange-400", border: "border-orange-200 dark:border-orange-500/30", bg: "bg-orange-50/50 dark:bg-orange-500/5", icon: "bg-orange-500/10 border-orange-500/20" },
        { accent: "text-cyan-600 dark:text-cyan-400", border: "border-cyan-200 dark:border-cyan-500/30", bg: "bg-cyan-50/50 dark:bg-cyan-500/5", icon: "bg-cyan-500/10 border-cyan-500/20" },
    ];
    const color = PROFILE_COLORS[index % PROFILE_COLORS.length];

    // Meta badges
    const metaItems = [
        { label: "Scan Mode", val: profile["scan-mode"] },
        { label: "Feature Set", val: profile["feature-set"] },
        { label: "Virus Log", val: profile["av-virus-log"] },
        { label: "Mobile Malware", val: profile["mobile-malware-db"] },
        { label: "EMS Threat Feed", val: profile["ems-threat-feed"] },
        { label: "Extended Log", val: profile["extended-log"] },
        { label: "Sandbox Mode", val: profile["fortisandbox-mode"] },
    ];

    const activeProtocols = PROTOCOLS.filter(p => profile[p]);

    return (
        <div className={`border rounded-xl overflow-hidden ${color.border} shadow-sm dark:shadow-none bg-white dark:bg-gray-800`}>
            {/* Header */}
            <button
                onClick={() => setOpen(o => !o)}
                className={`w-full flex items-center gap-3 px-4 py-3 text-left transition-all ${open ? color.bg : "bg-white hover:bg-slate-50 dark:bg-gray-800/50 dark:hover:bg-gray-800"}`}
            >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 border ${color.icon}`}>
                    <Shield size={14} className={color.accent} />
                </div>
                <div className="flex-1 min-w-0">
                    <p className={`text-[13px] font-black ${color.accent}`}>{profile.name}</p>
                    {profile.comment && (
                        <p className="text-[10px] text-slate-500 dark:text-gray-400 truncate mt-0.5">{profile.comment}</p>
                    )}
                </div>
                {/* Quick badges */}
                <div className="hidden sm:flex items-center gap-1.5 flex-wrap">
                    <div className="flex flex-col gap-0.5">
                        <span className="text-[9px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest">
                            NAC Quarantine
                        </span>
                        <span className="text-[10px] font-mono text-slate-700 dark:text-gray-300">
                            infected={profile["nac-quar"]?.infected} · expiry={profile["nac-quar"]?.expiry}
                        </span>
                    </div>
                </div>
                <div className={`transition-transform duration-200 shrink-0 ml-2 ${open ? "rotate-180" : ""}`}>
                    <ChevronDown size={14} className="text-slate-400 dark:text-gray-400" />
                </div>
            </button>

            {open && (
                <div className="border-t border-slate-200 dark:border-gray-700/50">

                    {/* Meta Info Row */}
                    <div className="px-4 py-3 bg-slate-50 dark:bg-gray-800 border-b border-slate-200 dark:border-gray-700/40">
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-x-6 gap-y-3">
                            {metaItems.map(({ label, val }) => (
                                <div key={label} className="flex flex-col gap-1">
                                    <span className="text-[9px] font-bold text-slate-500 dark:text-gray-400 uppercase tracking-widest">
                                        {label}
                                    </span>
                                    <ValChip val={val} />
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Protocol Matrix Table */}
                    <div className="overflow-x-auto custom-scroll">
                        <table className="w-full text-left text-xs border-collapse bg-white dark:bg-gray-800">
                            <thead className="bg-slate-50 dark:bg-gray-900/70">
                                <tr className="border-b border-slate-200 dark:border-gray-900/50">
                                    <th className="px-4 py-2 text-[10px] font-bold text-slate-700 dark:text-gray-200 uppercase tracking-widest sticky left-0 whitespace-nowrap">
                                        Protocol
                                    </th>
                                    {PROTOCOL_FIELDS.map(f => (
                                        <th key={f.key} className="px-3 py-2 text-[10px] font-bold text-slate-700 dark:text-gray-200 uppercase tracking-widest whitespace-nowrap">
                                            {f.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-gray-700/30">
                                {activeProtocols.map(proto => {
                                    const cfg: ProtocolConfig = profile[proto] ?? {};
                                    return (
                                        <tr key={proto} className="hover:bg-slate-50 dark:hover:bg-gray-700/20 transition-colors">
                                            {/* Protocol name */}
                                            <td className="px-4 py-2.5 sticky left-0 whitespace-nowrap">
                                                <div className="flex items-center gap-2">
                                                    <span className="text-[11px] font-black uppercase text-slate-800 dark:text-gray-200">
                                                        {proto}
                                                    </span>
                                                </div>
                                            </td>
                                            {PROTOCOL_FIELDS.map(f => (
                                                <td key={f.key} className="px-3 py-2.5 whitespace-nowrap">
                                                    <ValChip val={cfg[f.key]} />
                                                </td>
                                            ))}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
};

// ── Main Component ────────────────────────────────────────────────────────────
export default function AntivirusProfiles({ asset_id, client_id }: { asset_id: any, client_id: string }) {
    const [profiles, setProfiles] = useState<AVProfile[]>([]);
    const [loading, setLoading] = useState(true);

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const res = await baseapi.get("/firewall_sections", {
                params: {
                    query: JSON.stringify({
                        ...(asset_id && client_id ? { asset_id, client_id } : {}),
                        section_name: "antivirus_profiles",
                    }),
                    limit: 1,
                }
            });
            if (res.data.status && res.data.data?.[0]) {
                const raw = res.data.data[0].results;
                const parsed = typeof raw === "string" ? JSON.parse(raw) : raw;
                setProfiles(parsed.items ?? []);
            } else showError("Failed to load antivirus profiles");
        } catch (e: any) { showError(e.message); }
        finally { setLoading(false); }
    }, [asset_id, client_id]);

    useEffect(() => { fetchData(); }, [fetchData]);

    return (
        <div className="flex flex-col gap-4">
            {/* Content */}
            {loading ? (
                <div className="flex items-center justify-center gap-2 py-20">
                    <Loader2 className="w-4 h-4 animate-spin text-green-600 dark:text-green-400" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-gray-400">
                        Loading antivirus profiles...
                    </span>
                </div>
            ) : profiles.length === 0 ? (
                <div className="flex flex-col items-center gap-2 py-20 opacity-40">
                    <Shield size={24} className="text-slate-400 dark:text-gray-400" />
                    <span className="text-[10px] font-bold uppercase tracking-widest text-slate-500 dark:text-gray-400">
                        No profiles found
                    </span>
                </div>
            ) : (
                <div className="space-y-4">
                    {profiles.map((profile, i) => (
                        <ProfileTable key={profile.name} profile={profile} index={i} />
                    ))}
                </div>
            )}
        </div>
    );
}
